import React, { useState, useMemo } from 'react';
import { FileText, FileSpreadsheet } from 'lucide-react';
import { Lead } from '../../../types';
import { LeadPromptSegment } from '../../../utils/tableUtils';
import { MobileDatasetHeader, ViewMode, SortOption, FilterOption } from './MobileDatasetHeader';
import { MobileDatasetTable } from './MobileDatasetTable';
import { MobileDatasetCard } from './MobileDatasetCard';
import { MobileDatasetDetailView } from './MobileDatasetDetailView';
import { MobileBottomSheet, bottomSheetStyles } from './MobileBottomSheet';

interface ReasoningCriterion {
  criterion_text: string;
  reasoning_text: string;
  satisfied: 'yes' | 'no' | 'unclear';
  source_urls: (string | any)[];
}

interface MobileDatasetViewProps {
  leads: Lead[];
  segments: LeadPromptSegment[];
  tableName?: string;
  isLoading?: boolean;
  onBack?: () => void;
  onShare?: () => void;
  onExportCSV?: () => void;
  onExportExcel?: () => void;
  onDelete?: () => void;
}

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'default', label: 'Default order' },
  { value: 'name_asc', label: 'Name (A-Z)' },
  { value: 'name_desc', label: 'Name (Z-A)' },
  { value: 'match_desc', label: 'Most matches first' },
  { value: 'match_asc', label: 'Least matches first' },
];

const FILTER_OPTIONS: { value: FilterOption; label: string }[] = [
  { value: 'all', label: 'All results' },
  { value: 'full_matches', label: 'Full matches only' },
  { value: 'partial_matches', label: 'Partial matches' },
  { value: 'no_matches', label: 'No matches' },
];

export const MobileDatasetView: React.FC<MobileDatasetViewProps> = ({
  leads,
  segments,
  tableName,
  isLoading = false,
  onShare,
  onExportCSV,
  onExportExcel,
  onDelete,
}) => {
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const [sortOption, setSortOption] = useState<SortOption>('default');
  const [filterOption, setFilterOption] = useState<FilterOption>('all');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showFullTitle, setShowFullTitle] = useState(false);

  // Process leads with sort and filter
  const processedLeads = useMemo(() => {
    let result = [...leads];

    // Filter
    if (filterOption === 'full_matches') {
      result = result.filter(lead => {
        const reasoning = lead.reasoning || {};
        const criteria = Object.values(reasoning) as ReasoningCriterion[];
        return criteria.length > 0 && criteria.every(c => c.satisfied === 'yes');
      });
    } else if (filterOption === 'partial_matches') {
      result = result.filter(lead => {
        const reasoning = lead.reasoning || {};
        const criteria = Object.values(reasoning) as ReasoningCriterion[];
        const matchCount = criteria.filter(c => c.satisfied === 'yes').length;
        return criteria.length > 0 && matchCount > 0 && matchCount < criteria.length;
      });
    } else if (filterOption === 'no_matches') {
      result = result.filter(lead => {
        const reasoning = lead.reasoning || {};
        const criteria = Object.values(reasoning) as ReasoningCriterion[];
        return criteria.length === 0 || criteria.every(c => c.satisfied !== 'yes');
      });
    }

    // Sort
    if (sortOption === 'name_asc') {
      result.sort((a, b) => {
        const nameA = (a.data?.Name || a.data?.name || '').toLowerCase();
        const nameB = (b.data?.Name || b.data?.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
    } else if (sortOption === 'name_desc') {
      result.sort((a, b) => {
        const nameA = (a.data?.Name || a.data?.name || '').toLowerCase();
        const nameB = (b.data?.Name || b.data?.name || '').toLowerCase();
        return nameB.localeCompare(nameA);
      });
    } else if (sortOption === 'match_desc') {
      result.sort((a, b) => {
        const matchA = Object.values(a.reasoning || {}).filter((c: any) => c.satisfied === 'yes').length;
        const matchB = Object.values(b.reasoning || {}).filter((c: any) => c.satisfied === 'yes').length;
        return matchB - matchA;
      });
    } else if (sortOption === 'match_asc') {
      result.sort((a, b) => {
        const matchA = Object.values(a.reasoning || {}).filter((c: any) => c.satisfied === 'yes').length;
        const matchB = Object.values(b.reasoning || {}).filter((c: any) => c.satisfied === 'yes').length;
        return matchA - matchB;
      });
    }

    return result;
  }, [leads, sortOption, filterOption]);

  const handleCardClick = (lead: Lead) => {
    setSelectedLead(lead);
  };

  const handleBack = () => {
    setSelectedLead(null);
  };

  const openMenu = (menu: 'sort' | 'filter' | 'export') => {
    setShowSortMenu(menu === 'sort');
    setShowFilterMenu(menu === 'filter');
    setShowExportMenu(menu === 'export');
  };

  // Show detail view if a lead is selected
  if (selectedLead) {
    return (
      <MobileDatasetDetailView
        lead={selectedLead}
        segments={segments}
        onBack={handleBack}
      />
    );
  }

  // Export options for bottom sheet
  const exportOptions = [
    ...(onExportCSV ? [{
      value: 'csv' as const,
      label: 'Export as CSV',
      description: 'Comma-separated values file',
      icon: <FileText className="w-5 h-5 text-gray-600" />,
    }] : []),
    ...(onExportExcel ? [{
      value: 'excel' as const,
      label: 'Export as Excel',
      description: 'Microsoft Excel spreadsheet',
      icon: <FileSpreadsheet className="w-5 h-5 text-emerald-600" />,
    }] : []),
  ];

  return (
    <div className="flex flex-col h-full bg-[#F5F5F7]">
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        ${bottomSheetStyles}
      `}</style>

      <MobileDatasetHeader
        tableName={tableName}
        leadCount={leads.length}
        isLoading={isLoading}
        showFullTitle={showFullTitle}
        onToggleFullTitle={() => setShowFullTitle(!showFullTitle)}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        sortOption={sortOption}
        filterOption={filterOption}
        onOpenSortMenu={() => openMenu('sort')}
        onOpenFilterMenu={() => openMenu('filter')}
        onOpenExportMenu={() => openMenu('export')}
        onClearFilters={() => { setSortOption('default'); setFilterOption('all'); }}
        onShare={onShare}
        onDelete={onDelete}
        hasExportOptions={!!(onExportCSV || onExportExcel)}
      />

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <LoadingSkeleton />
        ) : processedLeads.length === 0 ? (
          <EmptyState
            hasFilter={filterOption !== 'all'}
            onClearFilter={() => setFilterOption('all')}
          />
        ) : viewMode === 'cards' ? (
          <div className="p-4 space-y-3 pb-28">
            {processedLeads.map((lead, index) => (
              <div key={lead.id} className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center mt-4">
                  <span className="text-[11px] font-medium text-gray-500">{index + 1}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <MobileDatasetCard
                    lead={lead}
                    segments={segments}
                    onClick={() => handleCardClick(lead)}
                    index={index}
                    dataTour={index === 0 ? 'mobile-first-card' : undefined}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <MobileDatasetTable
            leads={processedLeads}
            onLeadClick={handleCardClick}
          />
        )}
      </div>

      {/* Sort Bottom Sheet */}
      <MobileBottomSheet
        isOpen={showSortMenu}
        onClose={() => setShowSortMenu(false)}
        title="Sort Results"
        options={SORT_OPTIONS}
        selectedValue={sortOption}
        onSelect={setSortOption}
      />

      {/* Filter Bottom Sheet */}
      <MobileBottomSheet
        isOpen={showFilterMenu}
        onClose={() => setShowFilterMenu(false)}
        title="Filter Results"
        options={FILTER_OPTIONS}
        selectedValue={filterOption}
        onSelect={setFilterOption}
      />

      {/* Export Bottom Sheet */}
      <MobileBottomSheet
        isOpen={showExportMenu}
        onClose={() => setShowExportMenu(false)}
        title="Export Dataset"
        options={exportOptions}
        onSelect={(value) => {
          if (value === 'csv') onExportCSV?.();
          if (value === 'excel') onExportExcel?.();
        }}
        variant="detailed"
      />
    </div>
  );
};

// Loading skeleton component
const LoadingSkeleton: React.FC = () => (
  <div className="p-4 space-y-3">
    {[1, 2, 3, 4].map((i) => (
      <div key={i} className="bg-white rounded-2xl p-4 animate-pulse">
        <div className="flex items-start gap-3">
          <div className="w-6 h-6 bg-gray-100 rounded-full flex-shrink-0" />
          <div className="flex-1">
            <div className="h-5 bg-gray-200 rounded w-2/3 mb-2" />
            <div className="h-4 bg-gray-100 rounded w-full" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

// Empty state component
interface EmptyStateProps {
  hasFilter: boolean;
  onClearFilter: () => void;
}

const EmptyState: React.FC<EmptyStateProps> = ({ hasFilter, onClearFilter }) => (
  <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
    <div className="text-[48px] mb-4 opacity-30">🔍</div>
    <h3 className="text-[18px] font-semibold text-gray-900 mb-2">
      {hasFilter ? 'No matches found' : 'No results yet'}
    </h3>
    <p className="text-[14px] text-gray-500 max-w-[280px]">
      {hasFilter ? 'Try adjusting your filters' : 'Results will appear here once the search is complete.'}
    </p>
    {hasFilter && (
      <button
        onClick={onClearFilter}
        className="mt-4 px-4 py-2 bg-gray-900 text-white rounded-lg text-[13px] font-medium"
      >
        Clear filters
      </button>
    )}
  </div>
);
